/**
 * Task detail — mirrors Enter Platform's Tasks panel.
 *
 * Jordan's ask (2026-08-12): "the tasks in Ctrl Enter should follow as closely
 * as possible the UX for Tasks in Platform". This is a structural port of
 * `chat-panel/` in enter-front-end — same sections, same headings, same copy,
 * same behaviour — rendered in OUR idiom: Tailwind, lucide, flat (platform uses
 * styled-components, MDI and card shadows, which our design system forbids).
 *
 * The three things that make it Platform's UX rather than a restyle:
 *
 *  1. The body is PARSED. A halted-claim task's opening comment is a fenced
 *     coding-engine dump; showing it verbatim (what we did) is a wall of
 *     machine output. `parseTaskReason` turns it into "Why it's halted".
 *  2. Resolve actions are real. Enter has no resolve endpoint — its own UI
 *     posts a SENTINEL comment that the backend acts on. Same mechanism here.
 *  3. `Claim Halted` tasks cannot be closed. Closing achieves nothing: the
 *     claim re-halts and Enter sends a fresh task, so platform hides its done
 *     controls and so do we. A close button there would be a lie.
 *
 * SCOPE: no claim strip (DOS/Payer/Charge). That needs /provider/hl7 +
 * /provider/payer, deliberately deferred — see the PR body.
 */

import { useRef, useState } from 'react';
import {
  AlertCircle,
  Bot,
  CalendarClock,
  Check,
  CheckCheck,
  ChevronLeft,
  ClipboardList,
  Code2,
  Copy,
  Info,
  Loader2,
  MessageSquare,
  Pencil,
  Play,
  PlayCircle,
  RefreshCw,
  User,
  X,
} from 'lucide-react';
import type {
  ClaimSummary,
  EnterTask,
  ParsedTaskReason,
  ResolveSuccessKind,
} from '@ctrl-enter/types';
import {
  MOVE_TO_COLLECTIONS,
  RESOLVE_SUCCESS_COPY,
  RESUME_CLAIM,
  WAITING_FOR_EMR_UPDATE,
  getResolveSuccessKind,
  getReasonSectionTitle,
  getResolveOptions,
  isClaimHaltedCategory,
  parseTaskReason,
} from '@ctrl-enter/types';
import { cn } from '../utils/cn';
import { useExternalLinks } from '../hooks/use-external-links';
import { useClaimSummaryQuery, useTaskCommentsQuery } from '../hooks/use-gateway-queries';

/**
 * Where "Edit claim in ENTER" points. Platform opens
 * `/{accountId}/claims/{claimId}/edit` on its own origin; we already treat
 * `platform.enter.health` as that origin elsewhere in the app.
 *
 * UNVERIFIED against a live account. It is only a link, so a wrong path is a
 * visible 404 the user reports rather than anything that touches data — the
 * cheap, loud kind of wrong.
 */
const PLATFORM_ORIGIN = 'https://platform.enter.health';

function claimEditorUrl(accountId: string, claimId: string): string {
  return `${PLATFORM_ORIGIN}/${encodeURIComponent(accountId)}/claims/${encodeURIComponent(
    claimId
  )}/edit`;
}

/**
 * Resolve-option layout, shared so the cards CANNOT drift apart.
 *
 * Jordan: "please make sure that the Resolution buttons are aligned!" — the
 * cards differ by TINT (blue for the recommended path, amber for the ones that
 * don't fix anything), and it was that difference that pushed their buttons off
 * each other's left edge. Every card therefore uses the same box model and only
 * swaps colour: identical border width, identical padding, so every button in
 * the section starts at the same x by construction rather than by several class
 * lists happening to agree.
 *
 * Rev 2 put TWO buttons in Option 1, so full-width is out — every button
 * instead shares a fixed `h-8` and identical padding, which keeps them aligned
 * on the baseline and on the left edge whatever the label says.
 */
const OPTION_CARD = 'mx-4 mb-2 rounded-lg border';
const OPTION_HEAD = 'flex items-start gap-2 px-3 pt-2.5 pb-2';
const OPTION_FOOT = 'px-3 pb-3';
const OPTION_TITLE = 'text-[12px] font-semibold';
const OPTION_BODY = 'text-[11px] text-muted-foreground leading-snug';

const RESOLVE_BUTTON_BASE = cn(
  'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md',
  'text-[12px] font-medium transition-colors',
  'disabled:opacity-50 disabled:cursor-not-allowed'
);
const RESOLVE_BUTTON_PRIMARY = cn(
  RESOLVE_BUTTON_BASE,
  'bg-primary text-primary-foreground hover:bg-primary/90'
);
const RESOLVE_BUTTON_OUTLINE = cn(
  RESOLVE_BUTTON_BASE,
  'border border-border bg-card text-foreground hover:bg-muted/40'
);
/** Amber outline — the action is available in Platform but inert here. */
const RESOLVE_BUTTON_ATTENTION = cn(
  RESOLVE_BUTTON_BASE,
  'border border-attention-border/60 bg-card text-attention-text'
);

const SEVERITY_LABEL: Record<EnterTask['severity'], string> = {
  urgent: 'Urgent',
  high: 'High',
  normal: 'Normal',
  low: 'Low',
};

/** Enter's automated senders — these get the robot line and the AI badge. */
function isEnterSender(name?: string): boolean {
  return name === 'Enter, Inc.' || name === 'Enter Support';
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function formatCreated(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Sections ───────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-4 pt-3 pb-1.5">
      {children}
    </p>
  );
}

/** The parsed "Why it's halted" card — platform's coding-reason-card. */
function ReasonCard({ parsed, task }: { parsed: ParsedTaskReason; task: EnterTask }) {
  const [showRaw, setShowRaw] = useState(false);
  const problems = parsed.problems ?? [];
  const created = formatCreated(task.createdAt);

  return (
    <div className="mx-4 rounded-lg border border-border bg-card">
      <div className="flex items-start gap-2 px-3 pt-2.5 pb-2 border-b border-border/50">
        <AlertCircle size={14} className="mt-0.5 shrink-0 text-destructive" />
        <p className="text-[13px] font-medium text-foreground">{parsed.title ?? 'Problem'}</p>
      </div>

      {parsed.explain && (
        <div className="px-3 py-2 border-b border-border/50">
          {parsed.explainLabel && (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mr-1.5">
              {parsed.explainLabel}
            </span>
          )}
          <span className="text-[12px] text-foreground/90 leading-snug">{parsed.explain}</span>
        </div>
      )}

      {problems.length > 0 && (
        <ul className="divide-y divide-border/40">
          {problems.map((problem, i) => (
            <li key={`${problem.slot}-${problem.who ?? ''}-${i}`} className="px-3 py-2">
              <p className="text-[12px] text-foreground">
                {problem.slot}
                {problem.who && <span className="text-muted-foreground"> · {problem.who}</span>}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {problem.why}
                {problem.value && (
                  // Struck through: this is the value Enter REJECTED, and showing
                  // it plainly reads as if it were the correct one.
                  <span className="ml-1 line-through">{problem.value}</span>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-2 border-t border-border/50">
        <div className="px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Missing
          </p>
          <p className="text-[11px] text-foreground/90 break-words">
            {parsed.missingLabel || '—'}
          </p>
        </div>
        <div className="px-3 py-2 border-l border-border/50">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Halted
          </p>
          <p className="text-[11px] text-foreground/90">{created ?? '—'}</p>
        </div>
      </div>

      {parsed.payloadRaw && (
        <div className="border-t border-border/50">
          <button
            type="button"
            onClick={() => setShowRaw(v => !v)}
            className="w-full flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <Code2 size={11} />
            {showRaw ? 'Hide' : 'Show'} {parsed.rawLabel ?? 'raw output'}
          </button>
          {showRaw && (
            <pre className="px-3 pb-2 text-[10px] text-muted-foreground whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
              {parsed.payloadRaw}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

/** Platform's resolve-section, with the state-changing options disabled. */

/**
 * CLAIM — patient identity for the task.
 *
 * Jordan, 2026-08-21: "the most important thing to show is the MRN — this is
 * what the user needs to be able to find the record in the EMR to fix the task."
 * So the MRN is the emphasised value here, not the patient name: the name tells
 * you who, the MRN is the thing you actually type into the EMR.
 *
 * It is COPYABLE for the same reason. Reading a chart number off a screen and
 * retyping it into another system is where transcription errors come from, and
 * in billing a mistyped MRN means working the wrong patient's chart.
 */

/**
 * Enter declares `patientDob` / `fromDos` as bare strings with no `format`, so
 * they may arrive as `1998-09-18` OR as a full ISO timestamp. Rendering raw
 * puts `1998-09-18T00:00:00.000Z` where a birth date belongs — on a card whose
 * whole job is being scannable. Formats when parseable, otherwise shows the
 * original rather than inventing a date.
 */
function formatDateOnly(value?: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

function ClaimCard({ claim }: { claim: ClaimSummary }) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  /**
   * `navigator.clipboard` is SECURE-CONTEXT ONLY — the same restriction that
   * made `crypto.randomUUID` explode over a LAN IP. Optional chaining stops it
   * throwing, but the button would then do nothing at all: no copy, no error,
   * no feedback. Since copying the MRN is the entire point of this card, fall
   * back to the legacy `execCommand` path, and only claim success when a copy
   * actually happened.
   */
  const copyMrn = () => {
    const mrn = claim.mrn;
    if (!mrn) return;

    const succeeded = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };

    const legacyCopy = (): boolean => {
      try {
        const el = document.createElement('textarea');
        el.value = mrn;
        // Off-screen rather than hidden: `display:none` is not selectable, so
        // the copy would silently no-op.
        el.setAttribute('readonly', '');
        el.style.position = 'fixed';
        el.style.top = '-1000px';
        document.body.appendChild(el);
        el.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(el);
        return ok;
      } catch {
        return false;
      }
    };

    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(mrn).then(succeeded, () => {
        if (legacyCopy()) succeeded();
        else setCopyFailed(true);
      });
      return;
    }
    if (legacyCopy()) succeeded();
    else setCopyFailed(true);
  };

  const money =
    typeof claim.chargeAmount === 'number'
      ? claim.chargeAmount.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
      : '—';

  return (
    // data-phi: patient name + DOB + MRN together are three HIPAA identifiers.
    // Sentry Session Replay masks this subtree (see services/sentry.ts).
    <div data-phi className="mx-4 rounded-lg border border-border bg-card">
      <div className="flex items-center gap-2 px-3 pt-2.5 pb-2">
        <ClipboardList size={14} className="shrink-0 text-muted-foreground" />
        <p className="text-[13px] font-medium text-foreground truncate">
          {claim.patientName ?? 'Unknown patient'}
        </p>
        {claim.patientDob && (
          <span className="text-[11px] text-muted-foreground whitespace-nowrap">
            DOB {formatDateOnly(claim.patientDob)}
          </span>
        )}
      </div>

      {/* The MRN gets its own row and the strongest treatment on this card. */}
      <div className="px-3 pb-2">
        <button
          type="button"
          onClick={copyMrn}
          disabled={!claim.mrn}
          title={claim.mrn ? 'Copy MRN' : 'Enter has no external id for this patient'}
          className={cn(
            'w-full flex items-center gap-2 px-2 py-1.5 rounded-md border transition-colors text-left',
            claim.mrn
              ? 'border-primary/30 bg-primary/5 hover:bg-primary/10'
              : 'border-border bg-muted/20 cursor-not-allowed'
          )}
        >
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            MRN
          </span>
          <span className="flex-1 min-w-0 truncate text-[13px] font-semibold text-foreground tabular-nums">
            {claim.mrn ?? 'Not provided by Enter'}
          </span>
          {claim.mrn &&
            (copied ? (
              <span className="inline-flex items-center gap-1 text-[10px] text-primary-text shrink-0">
                <Check size={11} /> Copied
              </span>
            ) : (
              <Copy size={12} className="text-muted-foreground shrink-0" />
            ))}
        </button>
        {copyFailed && (
          <p className="mt-1 text-[10px] text-muted-foreground">
            Couldn't copy automatically — select the number above.
          </p>
        )}
      </div>

      <div className="grid grid-cols-3 border-t border-border/50">
        {[
          ['DOS', formatDateOnly(claim.fromDos) ?? '—'],
          ['Payer', claim.payerName ?? '—'],
          ['Charge', money],
        ].map(([label, value], i) => (
          <div key={label} className={cn('px-3 py-2 min-w-0', i > 0 && 'border-l border-border/50')}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {label}
            </p>
            <p className="text-[11px] text-foreground/90 truncate" title={value}>
              {value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResolveSection({
  task,
  onEmrUpdated,
  onEmrUpdatedAndClose,
  onResume,
  onCollections,
  submitting,
  closingNext,
  pendingAttention,
  resolvedKind,
  error,
}: {
  task: EnterTask;
  onEmrUpdated: () => void;
  onEmrUpdatedAndClose: () => void;
  onResume: () => void;
  onCollections: () => void;
  submitting: boolean;
  closingNext: boolean;
  /** Which amber action is in flight, so only that button shows a spinner. */
  pendingAttention: 'resume' | 'collections' | null;
  resolvedKind: ResolveSuccessKind | null;
  error: string | null;
}) {
  const { openInBrowser } = useExternalLinks();
  const categories = task.categoryNames ?? [];
  // One decision, not three flags — a collections task also gets the bypass
  // path, which is what keeps the Option 1-4 numbering stable.
  const { showResume, showCollections } = getResolveOptions(categories);

  if (resolvedKind) {
    const copy = RESOLVE_SUCCESS_COPY[resolvedKind];
    return (
      <>
        <SectionLabel>Resolved</SectionLabel>
        <div className="mx-4 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2.5 flex items-start gap-2">
          <CheckCheck size={14} className="mt-0.5 shrink-0 text-primary-text" />
          <div>
            <p className="text-[13px] font-medium text-foreground">{copy.title}</p>
            <p className="text-[11px] text-muted-foreground leading-snug">{copy.body}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SectionLabel>Resolve — choose one</SectionLabel>

      <div className="mx-4 flex items-start gap-1.5 pb-2">
        <Info size={11} className="mt-0.5 shrink-0 text-muted-foreground" />
        <p className="text-[11px] text-muted-foreground leading-snug">
          {/* Composed exactly as Platform composes it: the lead sentence depends
              on whether a bypass path exists, and collections appends its own. */}
          {showResume
            ? 'These are the ways to clear this halt.'
            : 'These are the only two ways to clear this halt.'}{' '}
          Chat can explain the issue, but it can’t change the claim. If nothing is changed, this
          claim will re-halt and send another Task.
          {showCollections &&
            ' Move this claim to patient/guarantor collections when insurance recovery is no longer the active path.'}
        </p>
      </div>

      {/* Option 1 — the recommended path, and the only write we ship enabled. */}
      <div className={cn(OPTION_CARD, 'border-primary/40 bg-primary/5')}>
        <div className={OPTION_HEAD}>
          <RefreshCw size={14} className="mt-0.5 shrink-0 text-primary-text" />
          <div className="min-w-0">
            <p className={cn(OPTION_TITLE, 'text-primary-text')}>Option 1 — Fix it in your EMR</p>
            <p className={OPTION_BODY}>
              Correct the encounter in your EMR, then mark it below. Your EMR syncs to ENTER
              automatically — instantly on some systems, within 12–24 hours on others.
            </p>
          </div>
        </div>
        <div className={cn(OPTION_FOOT, 'flex flex-wrap gap-2')}>
          <button
            type="button"
            onClick={onEmrUpdated}
            disabled={submitting || closingNext}
            className={RESOLVE_BUTTON_PRIMARY}
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {submitting ? 'Sending…' : "I've updated my EMR"}
          </button>
          {/* Second action added in rev 2 — same marker, but closes and advances
              in one press. A worklist is worked in a run, not one task at a time. */}
          <button
            type="button"
            onClick={onEmrUpdatedAndClose}
            disabled={submitting || closingNext}
            className={RESOLVE_BUTTON_OUTLINE}
          >
            {closingNext ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <CheckCheck size={12} />
            )}
            {closingNext ? 'Closing…' : 'Updated — close & next'}
          </button>
        </div>
        {error && <p className="px-3 pb-2.5 -mt-1 text-[10px] text-destructive">{error}</p>}
      </div>

      {/* Option 2 — a link out, so nothing is written from here. */}
      <div className={cn(OPTION_CARD, 'border-border bg-card')}>
        <div className={OPTION_HEAD}>
          <Pencil size={14} className="mt-0.5 shrink-0 text-primary-text" />
          <div className="min-w-0">
            <p className={cn(OPTION_TITLE, 'text-primary-text')}>Option 2 — Edit claim in ENTER</p>
            <p className={OPTION_BODY}>
              Open the claim editor to fix the missing detail and resubmit.
            </p>
          </div>
        </div>
        <div className={OPTION_FOOT}>
          <button
            type="button"
            disabled={!task.claimId}
            onClick={() => {
              if (task.claimId) void openInBrowser(claimEditorUrl(task.accountId, task.claimId));
            }}
            className={RESOLVE_BUTTON_PRIMARY}
          >
            <Pencil size={12} />
            {task.claimId ? 'Edit claim in ENTER' : 'No claim linked'}
          </button>
        </div>
      </div>

      {/*
        Options 3 and 4 carry Platform's ATTENTION treatment — amber, not blue —
        because neither fixes the underlying problem: one releases the claim
        with the check bypassed, the other hands the balance to the patient.
        They are rendered but NOT wired, by product decision: both change claim
        state and have never been exercised from Ctrl Enter.
      */}
      {showResume && (
        <AttentionOption
          title="Option 3 — Resume the claim & bypass this check"
          body="Nothing on the claim changes — ENTER releases it as-is and bypasses this check. Use this when you know the information is right and the check is wrong. If it denies, ENTER opens a denial task."
          action="Resume claim & bypass check"
          onAction={onResume}
          pending={pendingAttention === 'resume'}
          disabled={submitting || closingNext || pendingAttention !== null}
        />
      )}

      {showCollections && (
        <AttentionOption
          title="Option 4 — Move to collections"
          body="Flip responsibility to the patient/guarantor and route this claim to collections. ENTER generates an invoice and sends it — use this when insurance recovery is no longer the active path."
          action="Move to collections"
          onAction={onCollections}
          pending={pendingAttention === 'collections'}
          disabled={submitting || closingNext || pendingAttention !== null}
          // The ONLY resolve path that reaches the patient: Enter generates an
          // invoice and sends it. There is no undo from this panel, and at
          // sidebar width a misclick is cheap — so this one asks first.
          confirm="Send this to collections? Enter will invoice the patient directly."
        />
      )}
    </>
  );
}

/**
 * An Option 3/4 card — Platform's amber treatment, for the paths that do NOT
 * fix the underlying problem: one releases the claim with the check bypassed,
 * the other hands the balance to the patient.
 *
 * `confirm` opts a card into a two-step press. It is deliberately NOT on every
 * amber action: resuming a claim is recoverable (if the payer denies, Enter
 * simply opens a denial task, which its own copy says), whereas collections
 * sends a real invoice to a real person and this panel offers no undo. Only the
 * irreversible, patient-facing one asks twice.
 */
function AttentionOption({
  title,
  body,
  action,
  onAction,
  pending,
  disabled,
  confirm,
}: {
  title: string;
  body: string;
  action: string;
  onAction: () => void;
  pending: boolean;
  disabled: boolean;
  confirm?: string;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className={cn(OPTION_CARD, 'border-attention-border/50 bg-attention/5')}>
      <div className={OPTION_HEAD}>
        <PlayCircle size={14} className="mt-0.5 shrink-0 text-attention" />
        <div className="min-w-0">
          <p className={cn(OPTION_TITLE, 'text-attention-text')}>{title}</p>
          <p className={OPTION_BODY}>{body}</p>
        </div>
      </div>
      <div className={OPTION_FOOT}>
        {confirming && confirm ? (
          <div className="flex flex-col gap-1.5">
            <p className="text-[11px] font-medium text-attention-text leading-snug">
              {confirm}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setConfirming(false);
                  onAction();
                }}
                disabled={disabled}
                className={RESOLVE_BUTTON_ATTENTION}
              >
                <Play size={12} />
                Yes, {action.toLowerCase()}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className={RESOLVE_BUTTON_OUTLINE}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => (confirm ? setConfirming(true) : onAction())}
            disabled={disabled}
            className={RESOLVE_BUTTON_ATTENTION}
          >
            {pending ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            {pending ? 'Sending…' : action}
          </button>
        )}
      </div>
    </div>
  );
}


// ─── Panel ──────────────────────────────────────────────────────────────────

export function TaskDetailPanel({
  task,
  onBack,
  onClose,
  onPostComment,
  closing,
  onCloseTask,
  hasNext = false,
}: {
  task: EnterTask;
  /** Back to the task list. */
  onBack: () => void;
  /** Dismiss the whole panel (the header X, matching platform). */
  onClose?: () => void;
  /** Posts a comment body to the task; resolves false on failure. */
  onPostComment: (body: string) => Promise<boolean>;
  closing: boolean;
  onCloseTask: () => Promise<boolean>;
  /**
   * Whether another task follows this one in the list. Only changes the LABEL
   * — the caller decides where closing actually lands, so the copy can't
   * promise a "next" that isn't there.
   */
  hasNext?: boolean;
}) {
  /**
   * SYNCHRONOUS re-entry guard. The `busy` checks below read React state from
   * the render closure, and `disabled` only lands after a re-render — so two
   * clicks inside one frame can both see "not busy". I could not construct a
   * realistic human trigger for it, but the consequence here is a duplicate
   * `MOVE TO COLLECTIONS`, i.e. a second invoice to a patient, and a ref costs
   * nothing. Belt to the state's braces.
   */
  const inFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [closingNext, setClosingNext] = useState(false);
  const [pendingAttention, setPendingAttention] = useState<'resume' | 'collections' | null>(null);
  const [localResolved, setLocalResolved] = useState<ResolveSuccessKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [closeFailed, setCloseFailed] = useState(false);

  const parsed = parseTaskReason(task.body);
  const categories = task.categoryNames ?? [];
  const canCloseTask = !isClaimHaltedCategory(categories);
  const authorName = task.authorName ?? 'Unknown';
  const enterSender = isEnterSender(task.authorName);
  const created = formatCreated(task.createdAt);
  const due = task.dueDate ? formatCreated(task.dueDate) : null;
  // Only claim-linked tasks have anything to resolve — matches platform, which
  // renders nothing when there are no linked claims.
  const showResolve = !!task.claimId;

  // The thread is the source of truth for "already resolved" — local state only
  // covers the moment between pressing and the refetch landing. Without the
  // thread read, closing and reopening a task would re-offer an action that has
  // already been taken, and for collections that means invoicing twice.
  const commentsQuery = useTaskCommentsQuery(task.id, task.accountId);
  // Lazy: only once a task is open, and only when it actually has a claim.
  const claimQuery = useClaimSummaryQuery(task.accountId, task.claimId ?? '', !!task.claimId);
  const claim =
    claimQuery.data?.status === 200 ? claimQuery.data.body.claim : null;
  const threadResolved =
    commentsQuery.data?.status === 200
      ? getResolveSuccessKind(commentsQuery.data.body.comments)
      : null;
  const resolvedKind = localResolved ?? threadResolved;

  const busy = submitting || closingNext || pendingAttention !== null;

  /** Options 3 and 4: post the sentinel Enter's backend acts on. */
  const runAttentionAction = async (kind: 'resume' | 'collections') => {
    if (busy || resolvedKind || inFlight.current) return;
    inFlight.current = true;
    setPendingAttention(kind);
    setError(null);
    const marker = kind === 'resume' ? RESUME_CLAIM : MOVE_TO_COLLECTIONS;
    const ok = await onPostComment(marker);
    inFlight.current = false;
    setPendingAttention(null);
    if (ok) setLocalResolved(kind);
    else setError('Could not send that to Enter. Please try again.');
  };

  const handleEmrUpdated = async () => {
    if (busy || resolvedKind || inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    const ok = await onPostComment(WAITING_FOR_EMR_UPDATE);
    inFlight.current = false;
    setSubmitting(false);
    if (ok) setLocalResolved('emr');
    else setError('Could not send that to Enter. Please try again.');
  };

  /**
   * Post the marker AND close, in one press (rev 2's second button).
   *
   * Order matters and is deliberate: post first, close second. Closing a task
   * whose marker never landed would drop the work silently — Enter would never
   * learn the EMR was fixed — so a failed post aborts before the close. The
   * reverse order can't be recovered from.
   */
  const handleEmrUpdatedAndClose = async () => {
    if (busy || resolvedKind || inFlight.current) return;
    inFlight.current = true;
    setClosingNext(true);
    setError(null);
    const posted = await onPostComment(WAITING_FOR_EMR_UPDATE);
    if (!posted) {
      inFlight.current = false;
      setClosingNext(false);
      setError('Could not send that to Enter — nothing was closed. Please try again.');
      return;
    }
    const closed = await onCloseTask();
    inFlight.current = false;
    setClosingNext(false);
    if (!closed) {
      // The marker DID land, so the resolve itself succeeded — only the close
      // failed. Showing a generic failure here would tell the user to redo work
      // that is already done.
      setLocalResolved('emr');
      setCloseFailed(true);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header — platform's chat-header */}
      <div className="flex items-start gap-2 px-3 py-2.5 border-b border-border/60 sticky top-0 bg-background z-10">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to tasks"
          className="p-0.5 rounded-md text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors shrink-0"
        >
          <ChevronLeft size={16} />
        </button>

        <span
          className={cn(
            'flex items-center justify-center w-7 h-7 rounded-full shrink-0 text-[10px] font-semibold',
            enterSender ? 'bg-primary/15 text-primary-text' : 'bg-muted text-muted-foreground'
          )}
        >
          {enterSender ? <Bot size={14} /> : initialsOf(authorName)}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-[13px] font-semibold text-foreground truncate">{authorName}</p>
            {enterSender && (
              <span className="text-[9px] font-semibold px-1 py-px rounded bg-primary/15 text-primary-text shrink-0">
                AI
              </span>
            )}
          </div>
          {created && (
            <p className="text-[10px] text-muted-foreground truncate">
              {enterSender ? `Automated assistant · Created ${created}` : `Created ${created}`}
            </p>
          )}
        </div>

        <span
          className={cn(
            'inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap',
            task.open
              ? 'bg-attention/15 text-attention-text'
              : 'bg-muted text-muted-foreground'
          )}
        >
          <span
            className={cn(
              'w-1.5 h-1.5 rounded-full',
              task.open ? 'bg-attention' : 'bg-muted-foreground'
            )}
          />
          {/* Platform shouts this one — it is the only thing in the header that
              tells you the task is waiting on YOU. */}
          {task.open ? 'NEEDS YOU' : 'DONE'}
        </span>

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="p-0.5 rounded-md text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors shrink-0"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* "AI Message" kicker, above the title, as in Platform. */}
      {enterSender && (
        <div className="px-4 pt-3">
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary/10 text-primary-text">
            <Bot size={10} /> AI Message
          </span>
        </div>
      )}

      {/* The task subject is the heading here, not a row label — Platform gives
          it real weight, and at sidebar width it is the first thing scanned. */}
      {/* Subject and body are author-written and routinely quote patient
          names, DOBs and NPIs straight out of the coding engine. */}
      <p
        data-phi
        className={cn(
          'px-4 text-[15px] font-semibold text-foreground leading-snug',
          enterSender ? 'pt-1.5' : 'pt-3'
        )}
      >
        {task.title}
      </p>

      {/* Categories as pills — Platform renders EVERY category, and they are how
          a biller recognises the task type at a glance. */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-1 px-4 pt-2">
          {categories.map(name => (
            <span
              key={name}
              className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary-text"
            >
              {name}
            </span>
          ))}
        </div>
      )}

      {/* Carried over from the old expanded row so nothing was lost in the move. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pt-2 text-[11px] text-muted-foreground">
        <span>{SEVERITY_LABEL[task.severity]}</span>
        {due && (
          <span className="inline-flex items-center gap-1">
            <CalendarClock size={11} /> Due {due}
          </span>
        )}
        {task.commentCount > 0 && (
          <span className="inline-flex items-center gap-1">
            <MessageSquare size={11} /> {task.commentCount}
          </span>
        )}
        {/* Platform shows an assignee control here; we show the resolved name,
            and say "Unassigned" out loud rather than leaving a gap — an
            unclaimed task is the one most likely to be dropped. */}
        <span className="inline-flex items-center gap-1">
          <User size={11} /> {task.assigneeName ?? 'Unassigned'}
        </span>
      </div>

      {/* Reason — parsed when structured, verbatim when a human wrote it */}
      {parsed ? (
        <>
          <SectionLabel>{getReasonSectionTitle(task.title, categories)}</SectionLabel>
          <ReasonCard parsed={parsed} task={task} />
        </>
      ) : (
        task.body && (
          <>
            <SectionLabel>Details</SectionLabel>
            <p className="mx-4 text-[12px] text-foreground/90 whitespace-pre-wrap leading-snug">
              {task.body}
            </p>
          </>
        )
      )}

      {task.claimId && (
        <>
          <SectionLabel>Claim</SectionLabel>
          {claim ? (
            <ClaimCard claim={claim} />
          ) : (
            <p className="mx-4 text-[11px] text-muted-foreground">
              {claimQuery.isLoading
                ? 'Loading claim…'
                : // Explicit, because a silently missing MRN looks like the
                  // feature is broken rather than the data being absent.
                  'Could not load patient details for this claim.'}
            </p>
          )}
        </>
      )}

      {showResolve && (
        <div className="pt-1">
          <ResolveSection
            task={task}
            onEmrUpdated={() => void handleEmrUpdated()}
            onEmrUpdatedAndClose={() => void handleEmrUpdatedAndClose()}
            onResume={() => void runAttentionAction('resume')}
            onCollections={() => void runAttentionAction('collections')}
            submitting={submitting}
            closingNext={closingNext}
            pendingAttention={pendingAttention}
            resolvedKind={resolvedKind}
            error={error}
          />
        </div>
      )}

      <div className="mt-auto px-4 py-3">
        {canCloseTask ? (
          <>
            {/* A text link, not a button — Platform deliberately makes this
                quieter than the Resolve options, because closing is the
                secondary path and giving it button weight competes with them. */}
            <button
              type="button"
              onClick={() => {
                setCloseFailed(false);
                void onCloseTask().then(ok => setCloseFailed(!ok));
              }}
              disabled={closing || !task.open}
              className={cn(
                'inline-flex items-center gap-1.5 text-[12px] font-medium text-primary-text',
                'hover:underline transition-colors',
                'disabled:opacity-60 disabled:cursor-not-allowed disabled:no-underline'
              )}
            >
              {closing && <Loader2 size={12} className="animate-spin" />}
              {closing ? 'Closing…' : hasNext ? 'Close task & go to next' : 'Close task'}
            </button>
            {closeFailed && (
              <p className="mt-1 text-[10px] text-destructive">
                Couldn't close this task — it may be assigned to someone else.
              </p>
            )}
          </>
        ) : (
          // Not a disabled button: there is no state in which this becomes
          // clickable, so offering it at all would misdescribe how Enter works.
          <p className="text-[10px] text-muted-foreground/80 leading-snug">
            Halted-claim tasks close themselves once Enter re-checks the claim. Closing this by hand
            wouldn't fix anything — the claim would re-halt and send a new task.
          </p>
        )}
      </div>
    </div>
  );
}
